// Global Instructions Rule Applied!
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

// Agent Access — Phase 3 (consent + grant lifecycle).
//
// Two shapes live here:
//   1. globalConfigs.agent_access — the Connect-agent consent screen's option
//      set (enabled agents, the READ tool catalogue, consent policy), injected
//      by the backend global_configs controller from get_agent_config.
//      Permissive by design (.passthrough() + defaults), mirroring
//      editor_config_model: cfg evolves; the screen reads known keys.
//   2. The /agent/grants responses — strict-validated before any UI state
//      (CLAUDE.md: a response that fails validation is an error, not coerced).

// ── globalConfigs.agent_access ──────────────────────────────────────────────

export const AgentAccessAgentSchema = z
  .object({
    agent_key: z.string(),
    name: z.string(),
    environment: z.string().optional().default('global')
  })
  .passthrough()

export const AgentAccessToolSchema = z
  .object({
    tool_key: z.string(),
    // COALESCEd backend-side (tool_key / '') so always present strings.
    name: z.string().optional().default(''),
    description: z.string().optional().default(''),
    kind: z.string().optional().default('read'),
    // Phase 4.5: consent modes a write tool offers — 'propose' and/or
    // 'live'. A write tool without 'live' cannot be granted create-without-
    // approval access, however the UI is driven (create_agent_grant
    // re-checks it).
    allowed_modes: z.array(z.string()).optional().default(['propose'])
  })
  .passthrough()

export const AgentAccessConsentSchema = z
  .object({
    expiry_days_options: z.array(z.number()).optional().default([7, 30, 90]),
    expiry_days_default: z.number().optional().default(30),
    expiry_max_days: z.number().optional().default(365),
    disclosure: z.string().optional().default('')
  })
  .passthrough()

// Where to add a custom connector, per AI assistant. One cfg.agent_client_guides
// row each — vendor menu paths and plan gating drift, so they are data (with
// the vendor's own doc_url) rather than frontend copy, and adding another
// assistant is an INSERT rather than a code change.
export const AgentAccessClientSchema = z
  .object({
    client_key: z.string(),
    name: z.string().optional().default(''),
    path_hint: z.string().optional().default(''),
    steps_html: z.string().optional().default(''),
    notes_html: z.string().optional().default(''),
    doc_url: z.string().optional().default(''),
    sort_order: z.number().optional().default(100)
  })
  .passthrough()

// The connect-your-agent block on the My agents tab: the MCP connector URL a
// user pastes into their assistant (env-aware in cfg.app_config, so UAT and
// PROD each serve their own host) plus the per-assistant instructions. An empty
// connector_url is meaningful — it means this environment has no gateway
// configured, and the tab says so rather than offering the wrong host.
export const AgentAccessConnectionSchema = z
  .object({
    connector_url: z.string().optional().default(''),
    clients: z.array(AgentAccessClientSchema).optional().default([])
  })
  .passthrough()

export const AgentAccessConfigSchema = z
  .object({
    agents: z.array(AgentAccessAgentSchema).optional().default([]),
    tools: z.array(AgentAccessToolSchema).optional().default([]),
    // prefault: an absent section parses as `{}`, so the inner defaults fill it (Zod 4's `default` wants a full output value).
    consent: AgentAccessConsentSchema.prefault({}),
    connection: AgentAccessConnectionSchema.prefault({})
  })
  .passthrough()

export type AgentAccessConfig = z.infer<typeof AgentAccessConfigSchema>
export type AgentAccessAgent = z.infer<typeof AgentAccessAgentSchema>
export type AgentAccessTool = z.infer<typeof AgentAccessToolSchema>
export type AgentAccessClient = z.infer<typeof AgentAccessClientSchema>

// ── /agent/grants responses ─────────────────────────────────────────────────

export const AgentGrantScopesSchema = z
  .object({
    mode: z.string().optional().default('read'),
    tools: z.array(z.string()).optional().default([]),
    // Phase 4.5: the subset of `tools` consented to run without approval.
    live_tools: z.array(z.string()).optional().default([])
  })
  .passthrough()

export const AgentGrantSchema = z
  .object({
    agent_grants__id: z.string(),
    agent_grants__agent_id: z.string(),
    // COALESCEd to the agent key in the RPC — never null, even when the agent
    // was retired from cfg.agents after the grant was issued.
    agent_grants__agent_name: z.string(),
    agent_grants__scopes: AgentGrantScopesSchema,
    agent_grants__status: z.enum(['active', 'revoked']),
    agent_grants__expires_at: z.string(),
    agent_grants__date_created: z.string()
  })
  .strict()

export const AgentGrantsListSchema = z
  .object({
    grants: z.array(AgentGrantSchema)
  })
  .strict()

// create_agent_grant — still reachable, but only from the gateway's own OAuth
// consent page (the in-app connect button is gone); the frontend no longer
// calls it. Kept so the shape stays documented alongside the others.
export const AgentGrantCreatedSchema = z
  .object({
    agent_grants__id: z.string()
  })
  .strict()

// update_agent_grant edits the row in place, so the id is the one we sent;
// expires_at comes back so an edit-as-renewal can be confirmed without a
// re-read.
export const AgentGrantUpdatedSchema = z
  .object({
    agent_grants__id: z.string(),
    agent_grants__expires_at: z.string()
  })
  .strict()

export const AgentGrantRevokedSchema = z
  .object({
    agent_grants__id: z.string(),
    agent_grants__status: z.string()
  })
  .strict()

// ── /agent/products responses (admin "Agent Products" tab) ──────────────────

export const AgentProductSchema = z
  .object({
    agents__id: z.string(),
    agents__agent_key: z.string(),
    agents__name: z.string(),
    // COALESCEd in the RPC ('' / 'admin' / [] / 0) — never null, including on
    // rows seeded before the quarantine columns existed.
    agents__client_name_raw: z.string(),
    agents__source: z.string(),
    agents__enabled: z.boolean(),
    agents__environment: z.string(),
    agents__redirect_hosts: z.array(z.string()),
    agents__requests_per_minute: z.number(),
    agents__daily_row_limit: z.number(),
    agents__date_created: z.string()
  })
  .strict()

export const AgentProductsListSchema = z
  .object({
    products: z.array(AgentProductSchema)
  })
  .strict()

export const AgentProductEnabledSchema = z
  .object({
    agents__agent_key: z.string(),
    agents__enabled: z.boolean()
  })
  .strict()

export type AgentProduct = z.infer<typeof AgentProductSchema>

export function validateAgentProductsList(data: unknown): z.infer<typeof AgentProductsListSchema> {
  return validateWithZodSchema(AgentProductsListSchema, data, 'agent products list')
}

export function validateAgentProductEnabled(data: unknown): z.infer<typeof AgentProductEnabledSchema> {
  return validateWithZodSchema(AgentProductEnabledSchema, data, 'agent product enabled')
}

export type AgentGrant = z.infer<typeof AgentGrantSchema>
export type AgentGrantsList = z.infer<typeof AgentGrantsListSchema>

export function validateAgentGrantsList(data: unknown): AgentGrantsList {
  return validateWithZodSchema(AgentGrantsListSchema, data, 'agent grants list')
}

export function validateAgentGrantCreated(data: unknown): z.infer<typeof AgentGrantCreatedSchema> {
  return validateWithZodSchema(AgentGrantCreatedSchema, data, 'agent grant created')
}

export function validateAgentGrantUpdated(data: unknown): z.infer<typeof AgentGrantUpdatedSchema> {
  return validateWithZodSchema(AgentGrantUpdatedSchema, data, 'agent grant updated')
}

export function validateAgentGrantRevoked(data: unknown): z.infer<typeof AgentGrantRevokedSchema> {
  return validateWithZodSchema(AgentGrantRevokedSchema, data, 'agent grant revoked')
}

// ── /agent/proposals responses (Phase 4 — propose-approve writes) ───────────

export const AgentProposalSchema = z
  .object({
    agent_proposals__id: z.string(),
    agent_proposals__agent_id: z.string(),
    // COALESCEd to the agent key in the RPC — never null.
    agent_proposals__agent_name: z.string(),
    agent_proposals__tool: z.string(),
    // Nullable text / jsonb: summary and rationale may be null; payload is
    // the EXACT record an approval replays (design §6 — shown verbatim);
    // result is null until finalized (then a row ref or an error).
    agent_proposals__summary: z.string().nullable(),
    agent_proposals__rationale: z.string().nullable(),
    agent_proposals__payload: z.record(z.string(), z.unknown()),
    agent_proposals__status: z.enum([
      'pending', 'approved', 'rejected', 'applied', 'failed', 'undone'
    ]),
    // Phase 4.5: created live under a live-consented tool (vs applied after
    // the user approved it) — the tab labels provenance from this.
    agent_proposals__live: z.boolean(),
    agent_proposals__result: z.record(z.string(), z.unknown()).nullable(),
    // What the AGENT filed, present only when the approver edited the
    // proposal in the review form before approving it. Non-null is the
    // 'amended' flag — there is no separate boolean to fall out of step.
    agent_proposals__original_payload: z.record(z.string(), z.unknown()).nullable().optional(),
    agent_proposals__reviewed_at: z.string().nullable(),
    // WHO ACTIONED IT. An app admin can now approve, reject, retry or undo a
    // proposal from Agent Oversight, so "by you" is no longer a safe thing
    // for an owner-scoped list to assume.
    //   approved_by — whose session COMMITTED the write. Written by the
    //     approve/retry claim and never overwritten, so it survives an undo
    //     by the other party ("Approved by X" then "Undone by Y").
    //   reviewed_by — the LAST verdict: approve, reject or undo.
    // Both names are null until there is somebody to name; the RPC guards
    // them, because audit.resolve_user_name(NULL) returns 'System'.
    prog__approved_by_name: z.string().nullable(),
    prog__approved_by_is_me: z.boolean(),
    prog__reviewed_by_name: z.string().nullable(),
    prog__reviewed_by_is_me: z.boolean(),
    agent_proposals__date_created: z.string()
  })
  .strict()

export const AgentProposalsListSchema = z
  .object({
    proposals: z.array(AgentProposalSchema)
  })
  .strict()

// review response: rejected → {id, status, tool, payload}; approved → the
// finalized outcome {id, status: applied|failed, result}. Permissive union
// of both shapes, since the caller refreshes the list either way.
export const AgentProposalReviewedSchema = z
  .object({
    agent_proposals__id: z.string(),
    agent_proposals__status: z.string()
  })
  .passthrough()

// undo response: the claim plus, when the cluster was only partly reversed,
// the rows left in place (one sentence each). STRICT on both sides — a new
// key lands in the Python controller payload and here in the same change.
export const AgentProposalUndoneSchema = z
  .object({
    agent_proposals__id: z.string(),
    agent_proposals__status: z.literal('undone'),
    undo_residue: z.array(z.string()).optional()
  })
  .strict()

export type AgentProposal = z.infer<typeof AgentProposalSchema>
export type AgentProposalUndone = z.infer<typeof AgentProposalUndoneSchema>

export function validateAgentProposalsList(data: unknown): z.infer<typeof AgentProposalsListSchema> {
  return validateWithZodSchema(AgentProposalsListSchema, data, 'agent proposals list')
}

export function validateAgentProposalReviewed(data: unknown): z.infer<typeof AgentProposalReviewedSchema> {
  return validateWithZodSchema(AgentProposalReviewedSchema, data, 'agent proposal reviewed')
}

export function validateAgentProposalUndone(data: unknown): AgentProposalUndone {
  return validateWithZodSchema(AgentProposalUndoneSchema, data, 'agent proposal undone')
}

// Agent activity stat strip (GET /agent/activity/summary →
// view__agent_activity_summary). The RPC COALESCEs every number to 0, so all
// four keys are always present. STRICT on both sides — a new key must land in
// the RPC, the Python model (agent_activity_summary_record.py) and here in
// the same change or the route 500s.
export const AgentActivitySummarySchema = z
  .object({
    summary: z
      .object({
        prog__reads_today: z.number(),
        prog__rows_returned_today: z.number(),
        prog__writes_this_week: z.number(),
        prog__pending_review: z.number()
      })
      .strict()
  })
  .strict()

export type AgentActivitySummary = z.infer<typeof AgentActivitySummarySchema>['summary']

export function validateAgentActivitySummary(data: unknown): z.infer<typeof AgentActivitySummarySchema> {
  return validateWithZodSchema(AgentActivitySummarySchema, data, 'agent activity summary')
}
